import { CustomerDrawer } from "@/components/customers/customer-drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError } from "@/lib/api/api-error";
import { buildMetadata } from "@/lib/metadata";
import { useAuth } from "@/providers/auth";
import {
  type CustomerManagementPage,
  type CustomerManagementRecord,
  CustomerManagementService,
  type CustomerPriceHistoryEntry,
  type CustomerWritePayload,
} from "@/services/customer-management-service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
} from "lucide-react";
import { useDeferredValue, useState } from "react";
import { toast } from "sonner";

const PAGE_SIZE = 10;

export const Route = createFileRoute("/_main/customers/")({
  component: RouteComponent,
  loader: async () => {
    const initialPage = await CustomerManagementService.listPage({
      page: 1,
      pageSize: PAGE_SIZE,
    });
    return {
      initialPage,
    };
  },
  head: () => ({
    meta: buildMetadata({
      title: "Customers",
      description:
        "Manage customer records, deliveries, and historical pricing.",
      keywords: ["customers", "deliveries", "price history"],
    }),
  }),
});

function toNumber(value: number | string | null | undefined): number {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function formatCount(value: number): string {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value);
}

function formatQuantity(value: number | string): string {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 3,
  }).format(toNumber(value));
}

function formatInvoiceSetting(value: string): string {
  switch (value) {
    case "cod":
      return "COD";
    case "net_15":
      return "Net 15";
    case "net_30":
      return "Net 30";
    default:
      return "Not set";
  }
}

function formatMoney(value: number | string, currencyCode: string): string {
  const amount = toNumber(value);
  const code = currencyCode.trim().toUpperCase() || "SAR";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${code} ${new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
    }).format(amount)}`;
  }
}

function summarizeCustomerCount(
  page: number,
  pageSize: number,
  total: number,
  visible: number,
) {
  if (total === 0 || visible === 0) {
    return "Showing 0 customers";
  }
  const start = (page - 1) * pageSize + 1;
  const end = start + visible - 1;
  return `Showing ${start}-${end} of ${total} customers`;
}

function buildPriceMarkerPosition(entry: CustomerPriceHistoryEntry) {
  const min = toNumber(entry.min_price);
  const max = toNumber(entry.max_price);
  const latest = toNumber(entry.latest_price);
  if (max <= min) {
    return 50;
  }
  return Math.max(0, Math.min(100, ((latest - min) / (max - min)) * 100));
}

function buildRangePosition(
  value: number | string,
  minValue: number,
  maxValue: number,
) {
  const numericValue = toNumber(value);
  if (maxValue <= minValue) {
    return 50;
  }
  return Math.max(
    0,
    Math.min(100, ((numericValue - minValue) / (maxValue - minValue)) * 100),
  );
}

function RouteComponent() {
  const { initialPage } = Route.useLoaderData() as {
    initialPage: CustomerManagementPage | undefined;
  };
  const { user } = useAuth();
  const canManageCustomers = user?.supplierRole === "supplier_admin";
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [expandedCustomerId, setExpandedCustomerId] = useState<number | null>(
    null,
  );
  const [editingCustomer, setEditingCustomer] =
    useState<CustomerManagementRecord | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const customersQuery = useQuery({
    queryKey: ["customer-management", page, PAGE_SIZE, deferredSearch],
    queryFn: () =>
      CustomerManagementService.listPage({
        page,
        pageSize: PAGE_SIZE,
        search: deferredSearch,
      }),
    initialData: page === 1 && !deferredSearch.trim() ? initialPage : undefined,
  });

  const historyQuery = useQuery({
    queryKey: ["customer-price-history", expandedCustomerId],
    queryFn: () =>
      CustomerManagementService.getPriceHistory(expandedCustomerId as number),
    enabled: expandedCustomerId !== null,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CustomerWritePayload) =>
      CustomerManagementService.create(payload),
    onSuccess: async () => {
      toast.success("Customer added successfully.");
      setIsDrawerOpen(false);
      await queryClient.invalidateQueries({
        queryKey: ["customer-management"],
      });
    },
    onError: (error: unknown) => {
      let message = "Failed to add customer.";
      if (error instanceof ApiError) {
        message = error.message || message;
      }
      toast.error(message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      customerId,
      payload,
    }: {
      customerId: number;
      payload: Partial<CustomerWritePayload>;
    }) => CustomerManagementService.update(customerId, payload),
    onSuccess: async () => {
      toast.success("Customer updated successfully.");
      setEditingCustomer(null);
      setIsDrawerOpen(false);
      await queryClient.invalidateQueries({
        queryKey: ["customer-management"],
      });
    },
    onError: (error: unknown) => {
      let message = "Failed to update customer.";
      if (error instanceof ApiError) {
        message = error.message || message;
      }
      toast.error(message);
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => CustomerManagementService.syncWithErp(),
    onSuccess: async (result) => {
      toast.success(
        `ERP sync complete. Created ${result.summary.created}, updated ${result.summary.updated}.`,
      );
      await queryClient.invalidateQueries({
        queryKey: ["customer-management"],
      });
    },
    onError: (error: unknown) => {
      let message = "Customer sync failed.";
      if (error instanceof ApiError) {
        message = error.message || message;
      }
      toast.error(message);
    },
  });

  const invoiceMutation = useMutation({
    mutationFn: ({
      customerId,
      invoiceSetting,
    }: {
      customerId: number;
      invoiceSetting: string;
    }) =>
      CustomerManagementService.update(customerId, {
        invoice_frequency_flag: invoiceSetting,
      }),
    onSuccess: async () => {
      toast.success("Invoice setting updated.");
      await queryClient.invalidateQueries({
        queryKey: ["customer-management"],
      });
    },
    onError: (error: unknown) => {
      let message = "Could not update the invoice setting.";
      if (error instanceof ApiError) {
        message = error.message || message;
      }
      toast.error(message);
    },
  });

  const pageData = customersQuery.data;
  const customers = pageData?.results ?? [];
  const pageCount = Math.max(Math.ceil((pageData?.count ?? 0) / PAGE_SIZE), 1);
  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const visibleCustomerCount = customers.length;
  const openAddDrawer = () => {
    setEditingCustomer(null);
    setIsDrawerOpen(true);
  };

  const openEditDrawer = (customer: CustomerManagementRecord) => {
    setEditingCustomer(customer);
    setIsDrawerOpen(true);
  };

  const toggleExpanded = (customerId: number) => {
    setExpandedCustomerId((current) =>
      current === customerId ? null : customerId,
    );
  };

  const handleSubmit = async (payload: CustomerWritePayload) => {
    if (editingCustomer) {
      await updateMutation.mutateAsync({
        customerId: editingCustomer.id,
        payload,
      });
      return;
    }
    await createMutation.mutateAsync(payload);
  };

  const summaryText = summarizeCustomerCount(
    page,
    PAGE_SIZE,
    pageData?.count ?? 0,
    visibleCustomerCount,
  );

  return (
    <>
      <CustomerDrawer
        customer={editingCustomer}
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
      />

      <div className="w-full space-y-8 px-4 pt-6 pb-28 md:ml-[14px] md:w-[calc(100%-28px)] md:max-w-[1122px] md:px-0 md:pt-10 md:pb-8">
        <div className="hidden space-y-1.5 md:block">
          <h1 className="font-medium text-2xl text-[#212529]">Customers</h1>
          <p className="text-[#6c757d] text-base">
            Manage customer accounts, credit limits, and purchase history.
          </p>
        </div>

        <section className="space-y-5">
          <div className="space-y-3">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="w-full md:w-[340px]">
                <div className="relative">
                  <Search
                    aria-hidden="true"
                    className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3.5 size-[18px] text-[#6c757d]"
                  />
                  <Input
                    aria-label="Search customers"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                    placeholder="Search customers..."
                    className="h-[45px] rounded-lg border-[#dee2e6] bg-white pl-10 text-base"
                  />
                </div>
              </div>

              {canManageCustomers ? (
                <div className="hidden items-center gap-3 md:flex">
                  <Button
                    variant="outline"
                    className="h-[45px] rounded-lg border-[#ced4da] px-4"
                    onClick={() => syncMutation.mutate()}
                    disabled={syncMutation.isPending}
                  >
                    {syncMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <RefreshCw className="size-4" />
                    )}
                    Sync with ERP
                  </Button>
                  <Button
                    className="h-[45px] rounded-lg bg-black px-5 text-white hover:bg-black/90"
                    onClick={openAddDrawer}
                  >
                    <Plus className="size-4" />
                    Add customer
                  </Button>
                </div>
              ) : null}
            </div>

            {canManageCustomers ? (
              <Button
                variant="outline"
                className="h-[45px] w-full rounded-lg border-[#ced4da] md:hidden"
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
              >
                {syncMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RefreshCw className="size-4" />
                )}
                Sync with ERP
              </Button>
            ) : null}

            <div className="text-[#6c757d] text-sm">{summaryText}</div>
          </div>

          {customersQuery.isError ? (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <div>
                <p className="font-medium">Could not load customers</p>
                <p className="mt-1 text-sm">
                  Check your connection and try refreshing the page.
                </p>
              </div>
            </div>
          ) : null}

          <div className="hidden overflow-hidden rounded-xl border border-[#e9ecef] md:block">
            <div className="grid grid-cols-[44px_1.9fr_0.7fr_1.3fr_0.8fr_48px] border-[#e9ecef] border-b bg-[#f8f9fa] px-2 py-3 text-[#6c757d] text-xs">
              <div />
              <div className="px-3">Customer name</div>
              <div className="px-3">Deliveries</div>
              <div className="px-3">Credit limit</div>
              <div className="px-3">Invoice</div>
              <div className="px-3 text-right">Edit</div>
            </div>

            {customersQuery.isError ? null : customersQuery.isLoading ? (
              <div className="flex items-center justify-center px-6 py-14 text-muted-foreground">
                <Loader2 className="mr-2 size-4 animate-spin" />
                Loading customers...
              </div>
            ) : customers.length === 0 ? (
              <div className="px-6 py-14 text-center text-muted-foreground">
                No customers found.
              </div>
            ) : (
              customers.map((customer) => {
                const isExpanded = expandedCustomerId === customer.id;
                return (
                  <div
                    key={customer.id}
                    className="border-[#e9ecef] border-b last:border-b-0"
                  >
                    <div className="grid min-h-16 grid-cols-[44px_1.9fr_0.7fr_1.3fr_0.8fr_48px] items-center px-2 py-2 text-sm transition-colors hover:bg-[#f8f9fa]">
                      <div className="px-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg"
                          aria-label={`${isExpanded ? "Collapse" : "Expand"} price history for ${
                            customer.company_name ||
                            customer.contact_name ||
                            "Unnamed customer"
                          }`}
                          onClick={() => toggleExpanded(customer.id)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="size-4" />
                          ) : (
                            <ChevronRight className="size-4" />
                          )}
                        </Button>
                      </div>
                      <div className="px-3 py-2">
                        <div className="font-medium text-[#212529] text-base">
                          {customer.company_name ||
                            customer.contact_name ||
                            "Unnamed customer"}
                        </div>
                      </div>
                      <div className="px-3 py-2 font-medium text-[#212529]">
                        {formatCount(customer.deliveries_count)}
                      </div>
                      <div className="px-3 py-2">
                        <CreditLimitDisplay
                          creditLimit={customer.credit_limit}
                          currencyCode={customer.default_currency || "SAR"}
                        />
                      </div>
                      <div className="px-3 py-2">
                        <InvoiceSettingControl
                          customer={customer}
                          canManage={canManageCustomers}
                          isSaving={
                            invoiceMutation.isPending &&
                            invoiceMutation.variables?.customerId ===
                              customer.id
                          }
                          onChange={(invoiceSetting) =>
                            invoiceMutation.mutate({
                              customerId: customer.id,
                              invoiceSetting,
                            })
                          }
                        />
                      </div>
                      <div className="flex justify-end px-3 py-2">
                        {canManageCustomers ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-lg"
                            aria-label={`Edit ${
                              customer.company_name ||
                              customer.contact_name ||
                              "Unnamed customer"
                            }`}
                            onClick={() => openEditDrawer(customer)}
                          >
                            <Pencil className="size-4" />
                          </Button>
                        ) : null}
                      </div>
                    </div>

                    {isExpanded ? (
                      <div className="border-[#e9ecef] border-t bg-[#f8f9fa] px-5 py-3">
                        <div className="mb-2 font-medium text-[#6c757d] text-sm">
                          SKUs Sold ({historyQuery.data?.length ?? 0})
                        </div>
                        <div className="space-y-2.5">
                          <PriceHistoryPanel
                            entries={historyQuery.data ?? []}
                            isLoading={historyQuery.isLoading}
                            isError={historyQuery.isError}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>

          <div className="space-y-2.5 md:hidden">
            {customersQuery.isError ? null : customersQuery.isLoading ? (
              <div className="flex items-center justify-center px-4 py-10 text-muted-foreground">
                <Loader2 className="mr-2 size-4 animate-spin" />
                Loading customers...
              </div>
            ) : customers.length === 0 ? (
              <div className="px-4 py-10 text-center text-muted-foreground">
                No customers found.
              </div>
            ) : (
              customers.map((customer) => {
                const isExpanded = expandedCustomerId === customer.id;
                return (
                  <div
                    key={customer.id}
                    className="overflow-hidden rounded-xl border border-[#e9ecef] bg-white"
                  >
                    <div className="space-y-3 p-3.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-[#212529] text-base">
                            {customer.company_name ||
                              customer.contact_name ||
                              "Unnamed customer"}
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          {canManageCustomers ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-lg"
                              aria-label={`Edit ${
                                customer.company_name ||
                                customer.contact_name ||
                                "Unnamed customer"
                              }`}
                              onClick={() => openEditDrawer(customer)}
                            >
                              <Pencil className="size-4" />
                            </Button>
                          ) : null}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-lg"
                            aria-label={`${isExpanded ? "Collapse" : "Expand"} price history for ${
                              customer.company_name ||
                              customer.contact_name ||
                              "Unnamed customer"
                            }`}
                            onClick={() => toggleExpanded(customer.id)}
                          >
                            {isExpanded ? (
                              <ChevronDown className="size-4" />
                            ) : (
                              <ChevronRight className="size-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-[#6c757d] text-sm">
                          {formatCount(customer.deliveries_count)} deliveries
                        </span>
                        <span className="inline-flex rounded-md border border-[#ced4da] px-2.5 py-1 font-medium text-[#212529] text-sm">
                          {formatInvoiceSetting(
                            customer.invoice_frequency_flag,
                          )}
                        </span>
                      </div>

                      <div>
                        <CreditLimitDisplay
                          creditLimit={customer.credit_limit}
                          currencyCode={customer.default_currency || "SAR"}
                        />
                      </div>
                    </div>

                    {isExpanded ? (
                      <div className="border-[#e9ecef] border-t px-3.5 py-3">
                        <div className="mb-3 font-medium text-[#6c757d] text-sm">
                          SKUs Sold ({historyQuery.data?.length ?? 0})
                        </div>
                        <div className="space-y-3">
                          <PriceHistoryPanel
                            entries={historyQuery.data ?? []}
                            isLoading={historyQuery.isLoading}
                            isError={historyQuery.isError}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <span className="text-[#6c757d] text-sm">
              Page {page} of {pageCount}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg border-[#ced4da]"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(current - 1, 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg border-[#ced4da]"
                disabled={page >= pageCount}
                onClick={() =>
                  setPage((current) => Math.min(current + 1, pageCount))
                }
              >
                Next
              </Button>
            </div>
          </div>
        </section>

        {canManageCustomers ? (
          <div className="fixed inset-x-0 bottom-0 z-30 border-[#e9ecef] border-t bg-white px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] md:hidden">
            <Button
              className="h-12 w-full rounded-lg bg-[#102d03] text-base text-white hover:bg-[#102d03]/90"
              onClick={openAddDrawer}
            >
              <Plus className="size-[18px]" />
              Add customer
            </Button>
          </div>
        ) : null}
      </div>
    </>
  );
}

function CreditLimitDisplay({
  creditLimit,
  currencyCode,
}: {
  creditLimit: number | string | null | undefined;
  currencyCode: string;
}) {
  if (creditLimit === null || creditLimit === undefined || creditLimit === "") {
    return <span className="text-[#6c757d] text-sm">No limit set</span>;
  }

  return (
    <div className="flex items-center justify-between gap-3 md:block">
      <span className="text-[#6c757d] text-sm md:hidden">Credit limit</span>
      <span className="font-medium text-[#212529] text-sm">
        {formatMoney(creditLimit, currencyCode)}
      </span>
    </div>
  );
}

function InvoiceSettingControl({
  customer,
  canManage,
  isSaving,
  onChange,
}: {
  customer: CustomerManagementRecord;
  canManage: boolean;
  isSaving: boolean;
  onChange: (value: string) => void;
}) {
  if (!canManage) {
    return (
      <span className="inline-flex rounded-md border border-[#ced4da] px-2.5 py-1 font-medium text-[#212529] text-sm">
        {formatInvoiceSetting(customer.invoice_frequency_flag)}
      </span>
    );
  }

  return (
    <Select
      value={customer.invoice_frequency_flag || "__empty__"}
      onValueChange={(value) => onChange(value === "__empty__" ? "" : value)}
      disabled={isSaving}
    >
      <SelectTrigger
        aria-label={`Invoice setting for ${
          customer.company_name || customer.contact_name || "Unnamed customer"
        }`}
        className="h-9 w-[110px] rounded-lg border-[#ced4da]"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__empty__">Not set</SelectItem>
        <SelectItem value="cod">COD</SelectItem>
        <SelectItem value="net_15">Net 15</SelectItem>
        <SelectItem value="net_30">Net 30</SelectItem>
      </SelectContent>
    </Select>
  );
}

function PriceHistoryPanel({
  entries,
  isLoading,
  isError,
}: {
  entries: CustomerPriceHistoryEntry[];
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center py-4 text-muted-foreground text-sm">
        <Loader2 className="mr-2 size-4 animate-spin" />
        Loading historical price range...
      </div>
    );
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800 text-sm"
      >
        Could not load price history. Please try again.
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="py-4 text-muted-foreground text-sm">
        No historical delivered pricing found for this customer.
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {entries.map((entry) => (
        <div
          key={entry.product_key}
          className="rounded-xl border border-[#e9ecef] bg-white p-3.5 md:px-6 md:py-5"
        >
          {(() => {
            const minValue = toNumber(entry.min_price);
            const maxValue = toNumber(entry.max_price);
            const averagePosition = buildRangePosition(
              entry.average_price,
              minValue,
              maxValue,
            );
            const medianPosition = buildRangePosition(
              entry.median_price,
              minValue,
              maxValue,
            );
            const latestPosition = buildPriceMarkerPosition(entry);

            return (
              <div className="flex flex-col gap-4 md:grid md:grid-cols-[minmax(0,1fr)_90px_minmax(320px,1.5fr)] md:items-center md:gap-6">
                <div className="min-w-0">
                  <div className="font-medium text-[#212529] text-sm md:text-base">
                    {entry.product_name}
                  </div>
                  <div className="mt-1 text-[#6c757d] text-sm">
                    {entry.product_sku || "No SKU"}
                  </div>
                </div>

                <div className="flex items-center justify-between md:block">
                  <div className="text-[#6c757d] text-sm">Qty sold</div>
                  <div className="font-medium text-[#212529] text-sm md:mt-1 md:text-xl">
                    {formatQuantity(entry.qty_sold)}
                  </div>
                </div>

                <div aria-label={`Price range for ${entry.product_name}`}>
                  <div className="mb-2 flex justify-between text-xs">
                    <span className="text-[#6c757d]">Price range</span>
                    <span className="font-medium text-[#212529]">
                      Med {formatMoney(entry.median_price, entry.currency_code)}
                    </span>
                  </div>
                  <div className="relative h-6">
                    <div className="-translate-y-1/2 absolute inset-x-0 top-1/2 h-0.5 bg-[#adb5bd]" />
                    <div className="-translate-y-1/2 absolute top-1/2 left-0 h-3.5 w-0.5 bg-[#adb5bd]" />
                    <div className="-translate-y-1/2 absolute top-1/2 right-0 h-3.5 w-0.5 bg-[#adb5bd]" />
                    <div
                      className="-translate-x-1/2 absolute top-0 h-6 w-0.5 bg-[#ff6767]"
                      style={{ left: `${averagePosition}%` }}
                      title={`Average ${formatMoney(
                        entry.average_price,
                        entry.currency_code,
                      )}`}
                    />
                    <div
                      className="-translate-x-1/2 absolute top-0 h-6 w-0.5 bg-[#212529]"
                      style={{ left: `${medianPosition}%` }}
                      title={`Median ${formatMoney(
                        entry.median_price,
                        entry.currency_code,
                      )}`}
                    />
                    <div
                      className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 size-2 rounded-full border border-white bg-[#6ed378]"
                      style={{ left: `${latestPosition}%` }}
                      title={`Latest ${formatMoney(
                        entry.latest_price,
                        entry.currency_code,
                      )}`}
                    />
                  </div>
                  <div className="flex items-start justify-between text-[#6c757d] text-xs">
                    <span>
                      {formatMoney(entry.min_price, entry.currency_code)}
                    </span>
                    <span className="text-[#ff6767]">
                      Avg{" "}
                      {formatMoney(entry.average_price, entry.currency_code)}
                    </span>
                    <span>
                      {formatMoney(entry.max_price, entry.currency_code)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      ))}
    </div>
  );
}
